import Vue from 'vue';
import { mapActions, mapState } from 'vuex';
import inViewport from 'in-viewport';
import store from 'store';
import storefront from 'lib/storefront';
import * as gqlQueries from 'lib/graphql/queries';
import { i18n } from 'lib/i18n.js';
import { trackBannerView, trackBannerClick } from 'lib/tracking';
import { convertGQLToProductJson } from 'utils/general';
import { htmlDecode } from '../../assets/js/utils/general';

/**
 * Here, we take all of the independent
 * Vuex modules laid out in the store
 * subdirectories and create a single Vuex
 * store that includes all of them.
 */
export default (el) =>
  new Vue({
    i18n,
    el,
    store,
    name: 'Collection Page',
    components: {
      'product-card-cta-actions': () =>
        import('../product-card/product-card-cta-actions.vue'),
    },
    data() {
      return {
        ssEnabled: true,
        inSortBannerRenderedClass: 'in-sort-banner--rendered',
        renderedInSortBanners: [],
        bannersInjected: false,
        productsPerPage: window.__GLOBAL__.collection.productsPerPage,
      };
    },
    mounted() {
      if (this.ssEnabled) {
        window.addEventListener('searchspring.domReady', () => {
          // Remove all marketing cards when user activates sort/filter
          this.renderedInSortBanners.forEach((card) => {
            card.remove();
          });

          // Add the marketing cards except when user is filtering
          // This is because the card position setting becomes unreliable
          if (!window.location.hash.includes('filter:')) {
            setTimeout(() => {
              if (!this.bannersInjected) {
                this.injectInSortBanners();
                this.bannersInjected = true;
              }

              // Add to call stack so that product cards are rendered first
            }, 10);
          } else {
            this.bannersInjected = true;
          }
        });
      } else {
        this.bannersInjected = true;
      }
    },
    methods: {
      ...mapActions('global'),
      ...mapActions('quickView', ['fetchMicroCollection']),
      ...mapActions('cart', ['addToCart']),
      prepareBannerTrackingPayload(banner) {
        return {
          id: `collection:${banner.dataset.collectionHandle}|position:${banner.dataset.position}`,
          name: htmlDecode(banner.dataset.bannerName),
          location: 'PLP',
        };
      },
      handleBannerViewTracking(banner) {
        const payload = this.prepareBannerTrackingPayload(banner);
        trackBannerView({...payload, shopify_module_name: 'collection'});
      },
      handleBannerClickTracking(banner) {
        const payload = this.prepareBannerTrackingPayload(banner);
        trackBannerClick(payload);
      },
      injectInSortBanners() {
        const collectionContainer = this.$el.querySelector('.collection-grid');
        const inSortBanners = this.$el.querySelectorAll(
          '[data-in-sort-banner]',
        );

        const productCards = this.$el.querySelectorAll(
          '.collection-grid__item.ss__result',
        );

        inSortBanners.forEach(async (banner) => {
          const productHandle = banner.dataset.productHandle;
          if (productHandle !== '') {
            const product = await this.fetchProductData(productHandle);

            this.updateBannerButton(banner, product);
          }
          const position = parseInt(banner.dataset.position, 10);
          const page = parseInt(banner.dataset.pagePosition, 10);
          let currentPage = parseInt(
            window.location.search.split('page=')[1],
            10,
          );

          if (isNaN(currentPage)) {
            currentPage = 1;
          }

          const insertionIndex =
            this.productsPerPage * (page - 1) + position - 1;

          if (insertionIndex >= productCards.length) {
            return;
          }

          const clone = banner.cloneNode(true);
          clone.classList.add(this.inSortBannerRenderedClass);

          const productNode = productCards[insertionIndex];
          collectionContainer?.insertBefore(clone, productNode);

          inViewport(clone, this.handleBannerViewTracking);
          clone.addEventListener('click', () => {
            this.handleBannerClickTracking(clone);
          });

          const addButton = clone.querySelector('[data-banner-add]');
          const quickViewButton = clone.querySelector(
            '[data-banner-quick-view]',
          );

          addButton?.addEventListener('click', (e) => {
            e.preventDefault();

            this.addToCart({
              id: addButton?.dataset.id,
              quantity: 1,
            });
          });

          quickViewButton?.addEventListener('click', (e) => {
            e.preventDefault();
            window.KENDO.openQuickModalByHandle(quickViewButton.dataset.handle);
          });

          this.renderedInSortBanners.push(clone);
        });
      },
      async fetchProductData(handle) {
        const storefrontResponse = await storefront.request(
          gqlQueries.product,
          { handle },
        );
        const product = convertGQLToProductJson(storefrontResponse.product);
        return product;
      },
      updateBannerButton(banner, product) {
        if (!product) {
          console.error(`There was an error fetching a banner product`);
          return;
        }
        const btn = banner.querySelector('.in-sort-banner__cta');

        if (!product.available) {
          console.warn(`Banner product ${product.title} is unavailable`);
          return;
        }

        if (product.isBundle) {
          const link = `${Shopify.routes.root}products/${product.handle}`;
          btn.innerHTML = this.getBundleHTML(link);
        } else {
          const showQuickView = product.optionNames.find(
            (option) =>
              option.toLowerCase() === 'color' ||
              option.toLowerCase() === 'size',
          );

          btn.innerHTML = showQuickView
            ? this.getQuickViewHTML(product)
            : this.getAddToCartHTML(product);
        }
      },
      getQuickViewHTML(product) {
        return `<div data-banner-quick-view data-handle=${
          product.handle
        }><span>${this.$t('products.card.quick_shop')}</span></div>`;
      },
      getAddToCartHTML(product) {
        return `<div data-banner-add data-id=${
          product.variants[0].id
        }><span>${this.$t('products.card.add')}</span></div>`;
      },
      getBundleHTML(link) {
        return `<a href="${link}"><span>${this.$t(
          'products.card.view_details',
        )}</span></a>`;
      },
    },
    computed: {
      ...mapState('quickView', ['modalTitle']),
    },
  });
